/**
 * Shared Flowy UI text truncation: never cut mid-word.
 * Prefer a full sentence within budget; otherwise stop on a word boundary.
 */

/** ~2–3 lines at typical Flowy chip / status width (~40–50 chars/line). */
export const FLOWY_NODE_TEXT_MAX_CHARS = 120;

/** Soft word budget aligned with ~2–3 lines of readable UI text. */
export const FLOWY_NODE_TEXT_MAX_WORDS = 24;

/** Compact inline subjects (status lines, recommendation titles). */
export const FLOWY_INLINE_LABEL_MAX_CHARS = 64;

const ELLIPSIS = '…';

export type TruncateFlowyTextOptions = {
	maxChars?: number;
	maxWords?: number;
	/** Prefer keeping at least this many chars when snapping to a boundary. */
	minCharsKeep?: number;
	/**
	 * For prose shown in chat, allow the visible excerpt to run past the soft
	 * budget until the current sentence is complete. Labels should leave this
	 * disabled so an unpunctuated title cannot grow without bound.
	 */
	preserveCompleteSentence?: boolean;
};

const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const defaultMaxWords = (maxChars: number): number =>
	Math.max(4, Math.round(maxChars / 5));

/**
 * Soft character cap that never splits a word.
 * If the first token alone exceeds `maxChars`, it is kept intact (no mid-word cut).
 * Does not append an ellipsis — use {@link truncateFlowyText} when a … suffix is needed.
 */
export const truncateAtWordBoundary = (
	value: string | null | undefined,
	maxChars: number
): string => {
	const text = normalizeWhitespace(value || '');
	if (!text || text.length <= maxChars) return text;

	const sliced = text.slice(0, Math.max(0, maxChars));
	// Landing on a word end (or past the string) — keep the full last word.
	if (maxChars >= text.length || /\s/.test(text.charAt(maxChars))) {
		return sliced.trimEnd();
	}

	const lastSpace = sliced.lastIndexOf(' ');
	if (lastSpace > 0) {
		return sliced.slice(0, lastSpace).trimEnd();
	}
	return text.split(' ')[0] || text;
};

/**
 * Truncate Flowy-facing node / status text without mid-word cuts.
 * Returns the original string when already within limits; otherwise ends with ….
 */
export const truncateFlowyText = (
	value: string | null | undefined,
	options: TruncateFlowyTextOptions = {}
): string => {
	const maxChars = options.maxChars ?? FLOWY_NODE_TEXT_MAX_CHARS;
	const maxWords = options.maxWords ?? defaultMaxWords(maxChars);
	const minCharsKeep = options.minCharsKeep ?? Math.min(24, Math.max(8, Math.floor(maxChars / 2)));
	const preserveCompleteSentence = options.preserveCompleteSentence ?? false;

	const text = normalizeWhitespace(value || '');
	if (!text) return '';

	const words = text.split(' ').filter(Boolean);
	if (text.length <= maxChars && words.length <= maxWords) return text;

	let budget = maxChars;
	if (words.length > maxWords) {
		budget = Math.min(budget, words.slice(0, maxWords).join(' ').length);
	}
	budget = Math.max(1, budget);

	const window = text.slice(0, budget);

	// Prefer the last complete sentence that still fits.
	const sentenceEnd = /[.!?](?=\s|$)/g;
	let lastSentenceEnd = -1;
	let match: RegExpExecArray | null;
	while ((match = sentenceEnd.exec(window)) !== null) {
		lastSentenceEnd = match.index;
	}
	if (lastSentenceEnd >= minCharsKeep) {
		return `${window.slice(0, lastSentenceEnd + 1).trimEnd()}${ELLIPSIS}`;
	}

	// Chat excerpts should never look like a generation stopped halfway through
	// a thought. The character/word limits are soft here: finish the sentence
	// that crosses the budget, then indicate that later sentences were omitted.
	if (preserveCompleteSentence) {
		const nextSentenceEnd = text.slice(budget).search(/[.!?](?=\s|$)/);
		if (nextSentenceEnd >= 0) {
			const end = budget + nextSentenceEnd + 1;
			const completeSentence = text.slice(0, end).trimEnd();
			return end >= text.length ? completeSentence : `${completeSentence}${ELLIPSIS}`;
		}
	}

	// Otherwise break on a whole word (never mid-token).
	const clipped = truncateAtWordBoundary(text, budget)
		.replace(/[,:;–—-]+$/u, '')
		.trimEnd();

	if (!clipped) {
		return `${text.slice(0, Math.max(1, budget)).trimEnd()}${ELLIPSIS}`;
	}
	return `${clipped}${ELLIPSIS}`;
};
